import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { Button } from '../components/SubscriptionAppButton';
import { Badge } from '../components/SubscriptionAppBadge';
import { HelpCircle, ClipboardList, ChevronDown, ChevronUp, Trash2, GripVertical, Plus } from 'lucide-react';
interface CancellationSettingsPageProps {
  currentPage?: string;
  onNavigate?: (page: string) => void;
}
interface CancellationReason {
  id: string;
  reason: string;
  retentionOffer: string;
  showFeedback: boolean;
  requireFeedback: boolean;
}
export function CancellationSettingsPage({
  currentPage = 'Tools',
  onNavigate
}: CancellationSettingsPageProps) {
  const navigate = useNavigate();
  // State
  const [activeTab, setActiveTab] = useState<'cancellation' | 'pause'>('cancellation');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  // Cancellation Tab State
  const [cancellationFlowType, setCancellationFlowType] = useState('retention');
  const [customInstructions, setCustomInstructions] = useState('');
  const [cancellationReasons, setCancellationReasons] = useState<CancellationReason[]>([{
    id: '1',
    reason: 'Too expensive',
    retentionOffer: 'discount',
    showFeedback: true,
    requireFeedback: false
  }, {
    id: '2',
    reason: 'Not using it enough',
    retentionOffer: 'pause',
    showFeedback: true,
    requireFeedback: false
  }, {
    id: '3',
    reason: 'Changed my mind',
    retentionOffer: 'no-offer',
    showFeedback: false,
    requireFeedback: false
  }]);
  const [discountAmount, setDiscountAmount] = useState(15);
  const [discountDuration, setDiscountDuration] = useState('next-2');
  const [showAdditionalMessages, setShowAdditionalMessages] = useState(false);
  // Pause Tab State
  const [pauseFlowType, setPauseFlowType] = useState('retention');
  const [pauseReasons, setPauseReasons] = useState<CancellationReason[]>([{
    id: '1',
    reason: 'Going on vacation',
    retentionOffer: 'no-offer',
    showFeedback: false,
    requireFeedback: false
  }, {
    id: '2',
    reason: 'Need a break',
    retentionOffer: 'discount',
    showFeedback: true,
    requireFeedback: false
  }, {
    id: '3',
    reason: 'Too much product',
    retentionOffer: 'skip',
    showFeedback: false,
    requireFeedback: false
  }]);
  const [defaultPauseDuration, setDefaultPauseDuration] = useState('');
  // Navigation handler
  const handleNavigate = (page: string) => {
    if (onNavigate) {
      onNavigate(page);
    } else {
      if (page === 'Dashboard') navigate('/');else if (page === 'Subscriptions') navigate('/subscriptions');else if (page === 'Orders') navigate('/orders');else if (page === 'Tools') navigate('/management/tools');else navigate('/');
    }
  };
  const saveSetting = (message: string = 'Settings saved') => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };
  const handleAddReason = (isCancel: boolean) => {
    const newReason: CancellationReason = {
      id: Date.now().toString(),
      reason: '',
      retentionOffer: 'no-offer',
      showFeedback: false,
      requireFeedback: false
    };
    if (isCancel) {
      setCancellationReasons([...cancellationReasons, newReason]);
    } else {
      setPauseReasons([...pauseReasons, newReason]);
    }
    saveSetting();
  };
  const handleDeleteReason = (id: string, isCancel: boolean) => {
    if (isCancel) {
      setCancellationReasons(cancellationReasons.filter(r => r.id !== id));
    } else {
      setPauseReasons(pauseReasons.filter(r => r.id !== id));
    }
    saveSetting();
  };
  const handleUpdateReason = (id: string, field: keyof CancellationReason, value: any, isCancel: boolean) => {
    const updateReasons = (reasons: CancellationReason[]) => reasons.map(r => r.id === id ? {
      ...r,
      [field]: value
    } : r);
    if (isCancel) {
      setCancellationReasons(updateReasons(cancellationReasons));
    } else {
      setPauseReasons(updateReasons(pauseReasons));
    }
    saveSetting();
  };
  const hasDiscountOffer = cancellationReasons.some(r => r.retentionOffer === 'discount');
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-5078">
      {/* NavBar */}
      <div className="mb-6" data-id="element-5079">
        <NavBar tabs={[]} title="Cancellation Settings" subtitle="Configure how customers can cancel or pause their subscriptions. Set up retention offers to reduce churn." data-id="element-5080" />
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm mb-6" data-id="element-5081">
        <div className="flex gap-8 px-6" data-id="element-5082">
          <button onClick={() => setActiveTab('cancellation')} className={`h-12 text-[14px] font-medium border-b-2 transition-colors ${activeTab === 'cancellation' ? 'border-[#0EA5E9] text-[#0EA5E9]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-5083">
            Cancellation
          </button>
          <button onClick={() => setActiveTab('pause')} className={`h-12 text-[14px] font-medium border-b-2 transition-colors ${activeTab === 'pause' ? 'border-[#0EA5E9] text-[#0EA5E9]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-5084">
            Pause
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="space-y-6" data-id="element-5085">
        {/* CANCELLATION TAB */}
        {activeTab === 'cancellation' && <>
            {/* Section 1: Cancellation Flow Type */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-5086">
              <h2 className="text-[20px] font-semibold text-[#0F172A] mb-6" data-id="element-5087">
                Cancellation Flow Type
              </h2>
              <div className="space-y-3" data-id="element-5088">
                <label className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${cancellationFlowType === 'direct' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-5089">
                  <input type="radio" name="cancellationFlow" value="direct" checked={cancellationFlowType === 'direct'} onChange={e => {
                setCancellationFlowType(e.target.value);
                saveSetting();
              }} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-5090" />
                  <div className="flex-1" data-id="element-5091">
                    <div className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-5092">
                      Direct Cancellation
                    </div>
                    <p className="text-[14px] text-[#64748B]" data-id="element-5093">
                      Customers can cancel their subscription immediately
                      without any retention offers or additional steps
                    </p>
                  </div>
                </label>

                <label className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${cancellationFlowType === 'retention' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-5094">
                  <input type="radio" name="cancellationFlow" value="retention" checked={cancellationFlowType === 'retention'} onChange={e => {
                setCancellationFlowType(e.target.value);
                saveSetting();
              }} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-5095" />
                  <div className="flex-1" data-id="element-5096">
                    <div className="flex items-center gap-2 mb-1" data-id="element-5097">
                      <span className="text-[16px] font-semibold text-[#0F172A]" data-id="element-5098">
                        Retention Offers
                      </span>
                      <Badge variant="success" data-id="element-5099">Recommended</Badge>
                    </div>
                    <p className="text-[14px] text-[#64748B]" data-id="element-5100">
                      Present customers with alternative options (discounts,
                      pause, skip, etc.) based on their cancellation reason
                      before they cancel
                    </p>
                  </div>
                </label>

                <label className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${cancellationFlowType === 'custom' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-5101">
                  <input type="radio" name="cancellationFlow" value="custom" checked={cancellationFlowType === 'custom'} onChange={e => {
                setCancellationFlowType(e.target.value);
                saveSetting();
              }} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-5102" />
                  <div className="flex-1" data-id="element-5103">
                    <div className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-5104">
                      Custom Instructions
                    </div>
                    <p className="text-[14px] text-[#64748B] mb-3" data-id="element-5105">
                      Display custom instructions to customers explaining how to
                      cancel their subscription (e.g., 'Please contact support')
                    </p>
                    {cancellationFlowType === 'custom' && <textarea value={customInstructions} onChange={e => {
                  setCustomInstructions(e.target.value);
                  saveSetting();
                }} placeholder="Enter custom cancellation instructions..." className="w-full h-24 px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-5106" />}
                  </div>
                </label>
              </div>
            </div>

            {/* Section 2: Cancellation Reasons */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-5107">
              <h2 className="text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-5108">
                Cancellation Reasons
              </h2>
              <p className="text-[14px] text-[#64748B] mb-6" data-id="element-5109">
                Configure the reasons customers see when trying to cancel. Each
                reason can trigger different retention offers to help reduce
                churn.
              </p>

              <div className="space-y-4" data-id="element-5110">
                {cancellationReasons.map((reason, index) => <div key={reason.id} className="border border-[#E2E8F0] rounded-lg p-4" data-id="element-5111">
                    <div className="flex items-center gap-3 mb-4" data-id="element-5112">
                      <GripVertical className="w-5 h-5 text-[#94A3B8] cursor-move" data-id="element-5113" />
                      <span className="text-[14px] font-medium text-[#64748B]" data-id="element-5114">
                        {index + 1}. Reason
                      </span>
                      <div className="flex-1" data-id="element-5115" />
                      <button onClick={() => handleDeleteReason(reason.id, true)} className="p-2 hover:bg-[#FEE2E2] rounded-md transition-colors" data-id="element-5116">
                        <Trash2 className="w-4 h-4 text-[#EF4444]" data-id="element-5117" />
                      </button>
                    </div>

                    <div className="space-y-4" data-id="element-5118">
                      <div data-id="element-5119">
                        <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-5120">
                          Cancellation Reason
                        </label>
                        <input type="text" value={reason.reason} onChange={e => handleUpdateReason(reason.id, 'reason', e.target.value, true)} placeholder="e.g., Too expensive, Not using it enough" className="w-full h-[40px] px-3 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-5121" />
                        <p className="text-[12px] text-[#64748B] mt-1" data-id="element-5122">
                          This is what customers will see in the dropdown
                        </p>
                      </div>

                      <div data-id="element-5123">
                        <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-5124">
                          Retention Offer
                        </label>
                        <select value={reason.retentionOffer} onChange={e => handleUpdateReason(reason.id, 'retentionOffer', e.target.value, true)} className="w-full h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '16px 16px'
                  }} data-id="element-5125">
                          <option value="no-offer" data-id="element-5126">
                            No offer - Continue to cancel
                          </option>
                          <option value="discount" data-id="element-5127">
                            Offer Discount - Reduce price temporarily
                          </option>
                          <option value="swap" data-id="element-5128">
                            Swap Products - Change subscription items
                          </option>
                          <option value="gift" data-id="element-5129">
                            Gift Subscription - Change shipping address
                          </option>
                          <option value="skip" data-id="element-5130">
                            Skip Next Order - Delay upcoming delivery
                          </option>
                          <option value="pause" data-id="element-5131">
                            Pause Subscription - Temporarily stop deliveries
                          </option>
                          <option value="reschedule" data-id="element-5132">
                            Change Delivery Date - Reschedule next order
                          </option>
                          <option value="address" data-id="element-5133">
                            Update Address - Modify shipping location
                          </option>
                          <option value="frequency" data-id="element-5134">
                            Update Frequency - Change delivery schedule
                          </option>
                        </select>
                        <p className="text-[12px] text-[#64748B] mt-1" data-id="element-5135">
                          Choose what alternative to offer when this reason is
                          selected
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-4" data-id="element-5136">
                        <div data-id="element-5137">
                          <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-5138">
                            Show Feedback Box
                          </label>
                          <select value={reason.showFeedback ? 'yes' : 'no'} onChange={e => handleUpdateReason(reason.id, 'showFeedback', e.target.value === 'yes', true)} className="w-full h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 12px center',
                      backgroundSize: '16px 16px'
                    }} data-id="element-5139">
                            <option value="yes" data-id="element-5140">Yes</option>
                            <option value="no" data-id="element-5141">No</option>
                          </select>
                        </div>
                        <div data-id="element-5142">
                          <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-5143">
                            Require Feedback
                          </label>
                          <select value={reason.requireFeedback ? 'yes' : 'no'} onChange={e => handleUpdateReason(reason.id, 'requireFeedback', e.target.value === 'yes', true)} className="w-full h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 12px center',
                      backgroundSize: '16px 16px'
                    }} data-id="element-5144">
                            <option value="yes" data-id="element-5145">Yes</option>
                            <option value="no" data-id="element-5146">No</option>
                          </select>
                        </div>
                      </div>
                      <p className="text-[12px] text-[#64748B]" data-id="element-5147">
                        When enabled, customers must provide written feedback
                      </p>
                    </div>
                  </div>)}

                <button onClick={() => handleAddReason(true)} className="w-full h-[48px] flex items-center justify-center gap-2 border-2 border-dashed border-[#CBD5E1] rounded-lg text-[14px] font-medium text-[#0EA5E9] hover:bg-[#F0F9FF] hover:border-[#0EA5E9] transition-all" data-id="element-5148">
                  <Plus className="w-4 h-4" data-id="element-5149" />
                  Add More Reason
                </button>
              </div>
            </div>

            {/* Section 3: Discount Settings */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-5150">
              <h2 className="text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-5151">
                Discount Settings
              </h2>
              <p className="text-[14px] text-[#64748B] mb-6" data-id="element-5152">
                Configure how discounts work when offered as retention
                incentives. These settings apply to all discount offers.
              </p>

              {!hasDiscountOffer ? <div className="p-6 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg text-center" data-id="element-5153">
                  <p className="text-[14px] text-[#64748B]" data-id="element-5154">
                    No discount offers configured. Add a discount retention
                    offer above to see discount-specific settings here.
                  </p>
                </div> : <div className="space-y-4" data-id="element-5155">
                  <div className="grid grid-cols-2 gap-4" data-id="element-5156">
                    <div data-id="element-5157">
                      <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-5158">
                        Discount Amount
                      </label>
                      <div className="flex items-center gap-2" data-id="element-5159">
                        <input type="number" value={discountAmount} onChange={e => {
                    setDiscountAmount(parseInt(e.target.value) || 0);
                    saveSetting();
                  }} className="flex-1 h-[40px] px-3 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-5160" />
                        <span className="text-[14px] text-[#64748B]" data-id="element-5161">%</span>
                      </div>
                    </div>
                    <div data-id="element-5162">
                      <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-5163">
                        Discount Duration
                      </label>
                      <select value={discountDuration} onChange={e => {
                  setDiscountDuration(e.target.value);
                  saveSetting();
                }} className="w-full h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                  backgroundPosition: 'right 12px center',
                  backgroundSize: '16px 16px'
                }} data-id="element-5164">
                        <option value="this-order" data-id="element-5165">This order only</option>
                        <option value="next-2" data-id="element-5166">Next 2 orders</option>
                        <option value="next-3" data-id="element-5167">Next 3 orders</option>
                        <option value="ongoing" data-id="element-5168">Ongoing</option>
                      </select>
                    </div>
                  </div>
                </div>}
            </div>

            {/* Section 4: Default Retention Messages */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-5169">
              <h2 className="text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-5170">
                Default Retention Messages
              </h2>
              <p className="text-[14px] text-[#64748B] mb-6" data-id="element-5171">
                Set default messages for each retention offer type. These are
                used when no custom message is specified for a specific
                cancellation reason.
              </p>

              <div className="space-y-4" data-id="element-5172">
                <div data-id="element-5173">
                  <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-5174">
                    Discount Offer Message
                  </label>
                  <textarea defaultValue="We don't want to see you go! We would like to offer you a {{discountAmount}}% discount." onBlur={saveSetting} className="w-full h-20 px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-5175" />
                </div>

                <div data-id="element-5176">
                  <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-5177">
                    Gift/Change Address Offer Message
                  </label>
                  <textarea defaultValue="We don't want to see you go! Would you prefer to give the subscription as a gift by changing the shipping address?" onBlur={saveSetting} className="w-full h-20 px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-5178" />
                </div>

                <div data-id="element-5179">
                  <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-5180">
                    Pause Offer Message
                  </label>
                  <textarea defaultValue="We don't want to see you go! Would you prefer to pause your subscription instead of cancelling?" onBlur={saveSetting} className="w-full h-20 px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-5181" />
                </div>

                {/* Additional Messages (Collapsible) */}
                <div className="border-t border-[#E2E8F0] pt-4" data-id="element-5182">
                  <button onClick={() => setShowAdditionalMessages(!showAdditionalMessages)} className="flex items-center gap-2 text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-5183">
                    {showAdditionalMessages ? <ChevronUp className="w-4 h-4" data-id="element-5184" /> : <ChevronDown className="w-4 h-4" data-id="element-5185" />}
                    Additional Retention Offer Messages
                  </button>

                  {showAdditionalMessages && <div className="mt-4 space-y-4" data-id="element-5186">
                      <div data-id="element-5187">
                        <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-5188">
                          Product Swap Offer Message
                        </label>
                        <textarea defaultValue="We don't want to see you go! Would you prefer to swap out the products in your subscription?" onBlur={saveSetting} className="w-full h-20 px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-5189" />
                      </div>

                      <div data-id="element-5190">
                        <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-5191">
                          Skip Next Order Message
                        </label>
                        <textarea defaultValue="We don't want to see you go! How about you just skip the next order of your subscription?" onBlur={saveSetting} className="w-full h-20 px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-5192" />
                      </div>

                      <div data-id="element-5193">
                        <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-5194">
                          Reschedule Delivery Message
                        </label>
                        <textarea defaultValue="We don't want to see you go! Would you prefer to change your subscription date?" onBlur={saveSetting} className="w-full h-20 px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-5195" />
                      </div>

                      <div data-id="element-5196">
                        <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-5197">
                          Update Shipping Address Message
                        </label>
                        <textarea defaultValue="We don't want to see you go! Would you prefer to change your shipping address?" onBlur={saveSetting} className="w-full h-20 px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-5198" />
                      </div>

                      <div data-id="element-5199">
                        <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-5200">
                          Change Delivery Frequency Message
                        </label>
                        <textarea defaultValue="We don't want to see you go! Would you prefer to change the frequency of your subscription?" onBlur={saveSetting} className="w-full h-20 px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-5201" />
                      </div>
                    </div>}
                </div>
              </div>
            </div>
          </>}

        {/* PAUSE TAB */}
        {activeTab === 'pause' && <>
            {/* Section 1: Pause Flow Type */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-5202">
              <h2 className="text-[20px] font-semibold text-[#0F172A] mb-6" data-id="element-5203">
                Pause Flow Type
              </h2>
              <div className="space-y-3" data-id="element-5204">
                <label className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${pauseFlowType === 'direct' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-5205">
                  <input type="radio" name="pauseFlow" value="direct" checked={pauseFlowType === 'direct'} onChange={e => {
                setPauseFlowType(e.target.value);
                saveSetting();
              }} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-5206" />
                  <div className="flex-1" data-id="element-5207">
                    <div className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-5208">
                      Direct Pause
                    </div>
                    <p className="text-[14px] text-[#64748B]" data-id="element-5209">
                      Customers can pause their subscription immediately without
                      any retention offers
                    </p>
                  </div>
                </label>

                <label className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${pauseFlowType === 'retention' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-5210">
                  <input type="radio" name="pauseFlow" value="retention" checked={pauseFlowType === 'retention'} onChange={e => {
                setPauseFlowType(e.target.value);
                saveSetting();
              }} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-5211" />
                  <div className="flex-1" data-id="element-5212">
                    <div className="flex items-center gap-2 mb-1" data-id="element-5213">
                      <span className="text-[16px] font-semibold text-[#0F172A]" data-id="element-5214">
                        Retention Offers
                      </span>
                      <Badge variant="success" data-id="element-5215">Recommended</Badge>
                    </div>
                    <p className="text-[14px] text-[#64748B]" data-id="element-5216">
                      Present customers with alternative options (discounts,
                      swap products, etc.) based on their reason for pausing
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Section 2: Pause Reasons */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-5217">
              <h2 className="text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-5218">
                Pause Reasons
              </h2>
              <p className="text-[14px] text-[#64748B] mb-6" data-id="element-5219">
                Configure the reasons customers see when trying to pause. Each
                reason can trigger different retention offers.
              </p>

              <div className="space-y-4" data-id="element-5220">
                {pauseReasons.map((reason, index) => <div key={reason.id} className="border border-[#E2E8F0] rounded-lg p-4" data-id="element-5221">
                    <div className="flex items-center gap-3 mb-4" data-id="element-5222">
                      <GripVertical className="w-5 h-5 text-[#94A3B8] cursor-move" data-id="element-5223" />
                      <span className="text-[14px] font-medium text-[#64748B]" data-id="element-5224">
                        {index + 1}. Reason
                      </span>
                      <div className="flex-1" data-id="element-5225" />
                      <button onClick={() => handleDeleteReason(reason.id, false)} className="p-2 hover:bg-[#FEE2E2] rounded-md transition-colors" data-id="element-5226">
                        <Trash2 className="w-4 h-4 text-[#EF4444]" data-id="element-5227" />
                      </button>
                    </div>

                    <div className="space-y-4" data-id="element-5228">
                      <div data-id="element-5229">
                        <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-5230">
                          Pause Reason
                        </label>
                        <input type="text" value={reason.reason} onChange={e => handleUpdateReason(reason.id, 'reason', e.target.value, false)} placeholder="e.g., Going on vacation, Need a break" className="w-full h-[40px] px-3 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-5231" />
                      </div>

                      <div data-id="element-5232">
                        <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-5233">
                          Retention Offer
                        </label>
                        <select value={reason.retentionOffer} onChange={e => handleUpdateReason(reason.id, 'retentionOffer', e.target.value, false)} className="w-full h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '16px 16px'
                  }} data-id="element-5234">
                          <option value="no-offer" data-id="element-5235">
                            No offer - Continue to pause
                          </option>
                          <option value="discount" data-id="element-5236">Offer Discount</option>
                          <option value="swap" data-id="element-5237">Swap Products</option>
                          <option value="skip" data-id="element-5238">Skip Next Order</option>
                          <option value="reschedule" data-id="element-5239">
                            Change Delivery Date
                          </option>
                          <option value="frequency" data-id="element-5240">Update Frequency</option>
                        </select>
                      </div>

                      <div className="grid grid-cols-2 gap-4" data-id="element-5241">
                        <div data-id="element-5242">
                          <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-5243">
                            Show Feedback Box
                          </label>
                          <select value={reason.showFeedback ? 'yes' : 'no'} onChange={e => handleUpdateReason(reason.id, 'showFeedback', e.target.value === 'yes', false)} className="w-full h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 12px center',
                      backgroundSize: '16px 16px'
                    }} data-id="element-5244">
                            <option value="yes" data-id="element-5245">Yes</option>
                            <option value="no" data-id="element-5246">No</option>
                          </select>
                        </div>
                        <div data-id="element-5247">
                          <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-5248">
                            Require Feedback
                          </label>
                          <select value={reason.requireFeedback ? 'yes' : 'no'} onChange={e => handleUpdateReason(reason.id, 'requireFeedback', e.target.value === 'yes', false)} className="w-full h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 12px center',
                      backgroundSize: '16px 16px'
                    }} data-id="element-5249">
                            <option value="yes" data-id="element-5250">Yes</option>
                            <option value="no" data-id="element-5251">No</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>)}

                <button onClick={() => handleAddReason(false)} className="w-full h-[48px] flex items-center justify-center gap-2 border-2 border-dashed border-[#CBD5E1] rounded-lg text-[14px] font-medium text-[#0EA5E9] hover:bg-[#F0F9FF] hover:border-[#0EA5E9] transition-all" data-id="element-5252">
                  <Plus className="w-4 h-4" data-id="element-5253" />
                  Add More Reason
                </button>
              </div>
            </div>

            {/* Section 3: Pause Settings */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-5254">
              <h2 className="text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-5255">
                Pause Settings
              </h2>
              <p className="text-[14px] text-[#64748B] mb-6" data-id="element-5256">
                Configure how pause offers work when presented as retention
                incentives.
              </p>

              <div data-id="element-5257">
                <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-5258">
                  Default Pause Duration (Cycles)
                </label>
                <input type="number" value={defaultPauseDuration} onChange={e => {
              setDefaultPauseDuration(e.target.value);
              saveSetting();
            }} placeholder="Leave empty to let customer choose" className="w-full h-[40px] px-3 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-5259" />
                <p className="text-[12px] text-[#64748B] mt-1" data-id="element-5260">
                  Number of billing cycles to pause. Leave empty to let customer
                  choose.
                </p>
              </div>
            </div>

            {/* Section 4: Default Pause Retention Messages */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-5261">
              <h2 className="text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-5262">
                Default Pause Retention Messages
              </h2>
              <p className="text-[14px] text-[#64748B] mb-6" data-id="element-5263">
                Set default messages for pause retention offers.
              </p>

              <div className="space-y-4" data-id="element-5264">
                <div data-id="element-5265">
                  <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-5266">
                    Discount Offer Message
                  </label>
                  <textarea defaultValue="We don't want to see you go! We would like to offer you a {{discountAmount}}% discount." onBlur={saveSetting} className="w-full h-20 px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-5267" />
                </div>

                <div data-id="element-5268">
                  <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-5269">
                    Extended Pause Offer Message
                  </label>
                  <textarea defaultValue="Are you sure you want to pause your subscription?" onBlur={saveSetting} className="w-full h-20 px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-5270" />
                </div>

                <div data-id="element-5271">
                  <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-5272">
                    Decline Pause Button Text
                  </label>
                  <input type="text" defaultValue="No, pause the subscription" onBlur={saveSetting} className="w-full h-[40px] px-3 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-5273" />
                </div>
              </div>
            </div>
          </>}
      </div>

      {/* Toast Notification */}
      {showToast && <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-2 duration-200" data-id="element-5274">
          <div className="bg-white border border-[#CBD5E1] rounded-lg shadow-lg px-4 py-3 flex items-center gap-3 min-w-[320px]" data-id="element-5275">
            <div className="w-1 h-10 bg-[#10B981] rounded-full" data-id="element-5276" />
            <div className="flex-1" data-id="element-5277">
              <p className="text-[14px] font-semibold text-[#0F172A]" data-id="element-5278">
                Settings saved
              </p>
              <p className="text-[13px] text-[#64748B]" data-id="element-5279">{toastMessage}</p>
            </div>
          </div>
        </div>}
    </div>;
}